import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { findLast, values, keys } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import { EpoLoadingIndicator } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/epo-processing/epo-loading-indicator/epo-loading-indicator';

import './epo-processing.scss';

const PROCESS_STEP = [
  {
    successStateKey: ['isStarting', 'isProcessing'],
    copy: 'Processing your vehicle information',
  },
  {
    successStateKey: ['isAnalyzing'],
    copy: 'Analyzing thousands of local transactions',
  },
  {
    successStateKey: ['isPreparing', 'isCompleted'],
    copy: 'Searching for a no-obligation offer on your car',
  },
];

export const getProcessTimes = isMobile =>
  isMobile
    ? {
        processing: 1600,
        analyzing: 3000,
        preparing: 4800,
        completed: 6200,
      }
    : {
        processing: 1150,
        analyzing: 2000,
        preparing: 3200,
        completed: 4200,
      };

export class EpoProcessing extends Component {
  static propTypes = {
    className: PropTypes.string,
    isSubmittingFinished: PropTypes.bool.isRequired,
    setLoadingStatus: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
    forceLoading: PropTypes.bool,
    wrapperClassName: PropTypes.string,
    listClassName: PropTypes.string,
  };

  static defaultProps = {
    className: '',
    isSubmittingAppraisal: true,
    isMobile: false,
    forceLoading: false,
    wrapperClassName: 'mt-3 py-3_5',
    listClassName: 'pb-3',
  };

  constructor(props) {
    super(props);
    const { isMobile } = props;
    const { processing, analyzing, preparing, completed } = getProcessTimes(isMobile);
    this.processingTimeout = debounce(this.setProcessing, processing);
    this.analyzingTimeout = debounce(this.setAnalyzing, analyzing);
    this.preparingTimeout = debounce(this.setPreparing, preparing);
    this.completedTimeout = debounce(this.setCompleted, completed);
  }

  state = {
    isStarting: true,
    isAnalyzing: false,
    isProcessing: false,
    isPreparing: false,
    isCompleted: false,
  };

  componentDidMount() {
    this.processingTimeout();
    this.analyzingTimeout();
    this.preparingTimeout();
  }

  componentDidUpdate(prevProps) {
    const { isSubmittingFinished, forceLoading } = this.props;
    if (isSubmittingFinished && prevProps.isSubmittingFinished !== isSubmittingFinished) {
      this.completedTimeout();
    }
    if (forceLoading && prevProps.forceLoading !== forceLoading) {
      this.forceTimeouts();
    }
  }

  setProcessing = () => this.setState({ isProcessing: true });
  setAnalyzing = () => this.setState({ isAnalyzing: true });
  setPreparing = () => this.setState({ isPreparing: true });
  setCompleted = () => this.setState(() => ({ isCompleted: true }), this.props.setLoadingStatus);

  cancelPreviousTimeouts = () => {
    this.preparingTimeout.cancel();
    this.completedTimeout.cancel();
  };

  forceTimeouts = () => {
    this.cancelPreviousTimeouts();
    this.setCompleted();
  };

  componentWllUnmount() {
    this.processingTimeout.cancel();
    this.analyzingTimeout.cancel();
    this.cancelPreviousTimeouts();
  }

  render() {
    const { className, isSubmittingFinished, wrapperClassName, listClassName } = this.props;
    const { isStarting, isProcessing, isAnalyzing, isPreparing, isCompleted } = this.state;
    const steps = [{ isStarting }, { isProcessing }, { isAnalyzing }, { isPreparing }, { isCompleted }];
    const lastStep = findLast(steps, loadingState => !!values(loadingState)[0]);
    const currentStep = lastStep ? keys(lastStep)[0] : '';

    return (
      <div className={classnames('epo-processing mx-auto', className)}>
        <div className={wrapperClassName}>
          <EpoLoadingIndicator isSubmittingFinished={isSubmittingFinished} />
          <ul className={classnames('list-unstyled mt-2', listClassName)}>
            {PROCESS_STEP.map(({ successStateKey, copy }) => (
              <Fragment key={copy}>
                <li className="mb-1">
                  <div
                    className={classnames('fw-bold', {
                      'text-gray-darker': successStateKey.includes(currentStep),
                      'text-gray-light': !successStateKey.includes(currentStep),
                    })}
                  >
                    {copy}
                  </div>
                </li>
              </Fragment>
            ))}
          </ul>
        </div>
      </div>
    );
  }
}
